import { useRouter } from 'next/router';
import { useCallback } from 'react';

export interface UseOverlayRouteOptions {
  /** Query param that carries the open entry, e.g. "details". */
  param: string;
}

/**
 * Drives an overlay that has its own URL. The id rides on a query param, so
 * opening one is shallow and a shared link lands on the same state.
 */
export const useOverlayRoute = ({ param }: UseOverlayRouteOptions) => {
  const router = useRouter();

  const open = useCallback(
    (id: string) => {
      void router.push(
        { pathname: router.pathname, query: { ...router.query, [param]: id } },
        undefined,
        // Keeps the page behind the overlay where the reader left it.
        { shallow: true, scroll: false },
      );
    },
    [param, router],
  );

  /**
   * Swaps the open entry without adding to the history, so stepping through
   * entries leaves Back pointing at whatever came before the overlay.
   */
  const replace = useCallback(
    (id: string) => {
      void router.replace(
        { pathname: router.pathname, query: { ...router.query, [param]: id } },
        undefined,
        { shallow: true, scroll: false },
      );
    },
    [param, router],
  );

  /**
   * Replaces rather than pops: scroll restoration is off, so going back would
   * return the reader to the top of the list. Back still cannot reopen it.
   */
  const close = useCallback(() => {
    const { [param]: _entry, ...query } = router.query;

    void router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
      scroll: false,
    });
  }, [param, router]);

  return { open, replace, close };
};
